/**
 * 66.13, hold what's taken (the owner, DECISIONS 180: *"some mechanism to split units in foothold to hold A once captured
 * with a small force while the rest of the force pushes to take B"*; lane C's diagnosis on the Foothold slice preset,
 * Oct 13: a side that took one point of a sector lost it again before taking the other 36–52 % of the time, ~50 s after the
 * take, the taken point guarded by a median 0–3 people and by nobody 30–51 % of the time — the adaptive commander gave an
 * owned point a holder only once its picture showed an enemy at it, by when it was being retaken). With
 * `GameConfig.holdAndPush`, under an Operations push or tug: while a side owns a point of a sector in play and wants
 * another point of that sector, each such owned point keeps the fewest the threat needs — one squad, a second when the
 * side's picture puts a squad's worth of enemies within the support band of it — never more than half the side's free
 * squads; the rest push. On by default (it measured well: the push's attackers' first points lost before the other 36 →
 * 11 % and their sectors 15 → 20 at 12 a side; the 25-a-side tug +3 wins of 12 for the side with it, either seat); off per
 * side with false. Pure in the state.
 */
import type { CapturePoint, GameState } from '../types';
import { teamPicture } from '../core/picture';
import { sectorAt } from '../core/capturePoints';
import { SUPPORT_POSITION, rifleEffectivePeople } from '../core/support';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { vecDot, vecNormalize } from '../math';
import { dacos } from '../core/dmath';

/** Is hold-and-push on for `team` (the switch — one value or its side's, on unless false — under an Operations push or tug)? */
export function holdingAndPushing(state: Pick<GameState, 'config' | 'modeState'>, team: number): boolean {
  const v = state.config.holdAndPush;
  return (Array.isArray(v) ? v[team] !== false : v !== false) && !!state.modeState.points?.push;
}

/**
 * The points a side holds while it pushes another in the same sector, each with how many squads it keeps (1 or 2), in
 * point order; the total at most half `free` (rounded down, at least one while there are two free squads or more).
 */
export function holdsWhilePushing(state: GameState, team: number, sites: readonly CapturePoint[], free: number, squadSize: number, tick: number): Array<{ site: CapturePoint; squads: number }> {
  const ps = state.modeState.points;
  if (!ps?.front || free < 2) return [];
  const sectorOf = (s: CapturePoint) => sectorAt(ps.front!, s.surfaceDir);
  const wantedSectors = new Set(sites.filter(s => s.owner !== team || (s.capturer !== null && s.capturer !== team)).map(sectorOf));
  const owned = sites.filter(s => s.owner === team && (s.capturer === null || s.capturer === team) && wantedSectors.has(sectorOf(s))).sort((a, b) => a.id - b.id);
  if (!owned.length) return [];
  const r = state.planets[owned[0].planetIndex].radius, person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  const band = SUPPORT_POSITION.share * rifleEffectivePeople() * (1 + SUPPORT_POSITION.tolerance) * person;
  const known = teamPicture(state, team, tick).filter(b => b.planetIndex === owned[0].planetIndex);
  const arc = (a: { x: number; y: number; z: number }, b: { x: number; y: number; z: number }) => dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(a), vecNormalize(b))))) * r;
  let left = Math.max(1, Math.floor(free / 2));
  const out: Array<{ site: CapturePoint; squads: number }> = [];
  for (const s of owned) {
    if (left <= 0) break;
    const threat = known.filter(b => arc(b.surfaceDir, s.surfaceDir) <= band).length;
    const n = Math.min(left, threat >= squadSize ? 2 : 1);
    out.push({ site: s, squads: n });
    left -= n;
  }
  return out;
}
